import {
  MAX_AUDIO_FILE_SIZE,
  storePendingFile,
  usePendingFileStore,
} from "@bandwith/recordings";
import { useNavigate } from "@tanstack/react-router";
import { Mic, Upload } from "lucide-react";
import { useRef, useState } from "react";
import { useSession } from "../lib/auth";
import { useSignInRedirect } from "../lib/useSignInRedirect";
import { AudioRecorder } from "./AudioRecorder";
import { FileInput } from "./FileInput";

const ACCEPTED_EXTENSIONS = [".mp3", ".m4a", ".wav", ".ogg", ".webm", ".aac"];
const MAX_SIZE_MB = Math.round(MAX_AUDIO_FILE_SIZE / 1024 / 1024);

function isValidAudioFile(file: File): boolean {
  if (file.type.startsWith("audio/")) {
    return true;
  }
  const extension = file.name.toLowerCase().match(/\.[^.]+$/)?.[0];
  return extension ? ACCEPTED_EXTENSIONS.includes(extension) : false;
}

function validateFile(file: File): string | null {
  if (!isValidAudioFile(file)) {
    return "Please select an audio file (MP3, M4A, WAV, OGG, WebM, or AAC)";
  }
  if (file.size > MAX_AUDIO_FILE_SIZE) {
    return `File too large. Maximum size is ${MAX_SIZE_MB}MB`;
  }
  return null;
}

type Props = {
  /**
   * Called when recording/upload completes.
   * - If provided: shows inline AudioRecorder + FileInput when signed in
   * - If not provided: shows navigation links when signed in
   */
  onRecordingComplete?: (file: File) => void;
  /**
   * Style variant for the buttons
   * - "inline": Full-width buttons in a 2-column grid (song details page)
   * - "compact": Smaller inline buttons (about page)
   */
  variant?: "inline" | "compact";
  /**
   * Auto-start recording when component mounts (after returning from auth).
   * Only applies when onRecordingComplete is provided (inline mode).
   */
  autoStart?: boolean;
};

const inlineButtonStyles =
  "inline-flex w-full items-center justify-center gap-2 rounded-lg border border-border-subtle bg-bg-secondary px-4 py-3 text-sm font-medium text-text-secondary transition-colors hover:border-text-muted hover:text-text-primary";

const compactButtonStyles =
  "inline-flex items-center gap-1.5 rounded-md border border-border-subtle px-3 py-1.5 text-sm text-text-muted transition-colors hover:border-text-muted hover:text-text-secondary";

/**
 * Auth-aware recording actions component for the SPA.
 *
 * Handles two use cases:
 * 1. **Song details page**: Shows inline recorder when signed in, calls onRecordingComplete
 * 2. **About page**: Shows navigation links when signed in, navigates to /songs/new
 *
 * When signed out, both show "Sign In to Record" / "Sign In to Upload" buttons.
 */
export function RecordingActions({
  onRecordingComplete,
  variant = "inline",
  autoStart = false,
}: Props) {
  const { session } = useSession();
  const signInRedirect = useSignInRedirect();
  const isSignedIn = !!session?.user;
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const setPendingFile = usePendingFileStore((s) => s.setPendingFile);

  const buttonStyles =
    variant === "inline" ? inlineButtonStyles : compactButtonStyles;
  const containerStyles =
    variant === "inline"
      ? "mt-4 grid grid-cols-2 gap-2"
      : "flex items-center gap-2";

  const handleSignIn = (action: "record" | "upload") => {
    const callbackUrl = `/songs/new?action=${action}`;
    signInRedirect(callbackUrl);
  };

  const handleUploadClick = () => {
    setError(null);
    inputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    if (isSignedIn) {
      if (onRecordingComplete) {
        onRecordingComplete(file);
      } else {
        setPendingFile(file);
        navigate({ to: "/songs/new", search: { action: "upload" } });
      }
    } else {
      try {
        await storePendingFile(file);
      } catch {
        console.warn("Failed to store pending file in IndexedDB");
      }
      handleSignIn("upload");
    }
  };

  if (isSignedIn && onRecordingComplete) {
    return (
      <div className={containerStyles}>
        <AudioRecorder onSave={onRecordingComplete} autoStart={autoStart} />
        <FileInput onFileAdd={onRecordingComplete} />
      </div>
    );
  }

  const recordLabel = isSignedIn ? "Record" : "Sign In to Record";
  const uploadLabel = isSignedIn ? "Upload" : "Sign In to Upload";
  const handleRecordClick = isSignedIn
    ? () => navigate({ to: "/songs/new", search: { action: "record" } })
    : () => handleSignIn("record");

  return (
    <>
      <div className={containerStyles}>
        <button
          type="button"
          onClick={handleRecordClick}
          className={buttonStyles}
        >
          <Mic className="size-4 text-red-500" />
          {recordLabel}
        </button>
        <button
          type="button"
          onClick={handleUploadClick}
          className={buttonStyles}
        >
          <Upload className="size-4" />
          {uploadLabel}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept=".mp3,.m4a,.wav,.ogg,.webm,.aac,audio/*"
          onChange={handleFileChange}
          className="hidden"
        />
      </div>
      {error && (
        <p
          className={`text-sm text-red-500 ${variant === "inline" ? "mt-2" : ""}`}
        >
          {error}
        </p>
      )}
    </>
  );
}
